// Martian Logo & Asset System v1 (LOCKED) -- single registry of every brand
// asset path the app is allowed to reference. Components import from here;
// nothing else in src/ should hard-code a /brand/... URL.
//
// Governing rule: "GLIM speaks. ARC structures."
//   GLIM = the FACE  (icon, favicon, app icon, avatar, companion, states)
//   ARC  = the SYSTEM LANGUAGE (Intelligence Chain, diagrams, workflows)
//          -- never an icon, favicon, avatar or mascot.
//
// Files live in frontend/public/brand/** (copied from the LOCKED package;
// only production-master assets are shipped -- no reference mock-ups, no
// captioned state PNGs, no DRAFT boards).

const BASE = `${process.env.PUBLIC_URL || ''}/brand`;

export const BRAND_COLORS = Object.freeze({
  ink: '#0A0A0A',
  paper: '#F7F6F2',
  // Accent only: approval / attention / emphasis. Never a character fill.
  signal: '#E5482D',
  clay: '#C9A88A',
  clayDeep: '#A88466',
  cream: '#ECE4D6',
});

export const GLIM_ASSETS = Object.freeze({
  // Masters (512 viewBox). `svg` = warm clay on transparent.
  master: `${BASE}/glim/masters/glim_master.svg`,
  creamOnInk: `${BASE}/glim/svg/glim_cream_on_ink.svg`,
  monoInk: `${BASE}/glim/svg/glim_mono_ink.svg`,
  monoPaper: `${BASE}/glim/svg/glim_mono_paper.svg`,
  // Micro optical cut: body + sensor slit only (readable at 16 px).
  micro: `${BASE}/glim/svg/glim_favicon_32.svg`,
  icons: Object.freeze({
    favicon16: `${BASE}/glim/icons/favicon_16.png`,
    favicon32: `${BASE}/glim/icons/favicon_32.png`,
    favicon48: `${BASE}/glim/icons/favicon_48.png`,
    appleTouch180: `${BASE}/glim/icons/apple_touch_180.png`,
    appIcon512: `${BASE}/glim/icons/app_icon_512.png`,
    pwa192: `${BASE}/glim/icons/pwa_192.png`,
    pwa512: `${BASE}/glim/icons/pwa_512.png`,
    og: `${BASE}/glim/icons/og_share_1200x630.png`,
    socialAvatar: `${BASE}/glim/icons/social_avatar.png`,
  }),
  // Raster lockups from the locked package (external / print / social use).
  lockups: Object.freeze({
    horizontalLight: `${BASE}/glim/lockups/horizontal_lockup_light.png`,
    horizontalDark: `${BASE}/glim/lockups/horizontal_lockup_dark.png`,
    stackedLight: `${BASE}/glim/lockups/stacked_lockup_light.png`,
    stackedDark: `${BASE}/glim/lockups/stacked_lockup_dark.png`,
  }),
});

export const GLIM_STATE_FILES = Object.freeze({
  neutral: `${BASE}/glim/states/neutral_transparent.png`,
  curious: `${BASE}/glim/states/curious_transparent.png`,
  thinking: `${BASE}/glim/states/thinking_transparent.png`,
  discovering: `${BASE}/glim/states/discovering_transparent.png`,
  analyzing: `${BASE}/glim/states/analyzing_transparent.png`,
  recommending: `${BASE}/glim/states/recommending_transparent.png`,
  waiting: `${BASE}/glim/states/waiting_transparent.png`,
  approval_required: `${BASE}/glim/states/approval_transparent.png`,
  acting: `${BASE}/glim/states/acting_transparent.png`,
  success: `${BASE}/glim/states/success_transparent.png`,
  learning: `${BASE}/glim/states/learning_transparent.png`,
  warning: `${BASE}/glim/states/warning_transparent.png`,
  something_changed: `${BASE}/glim/states/changed_transparent.png`,
  error: `${BASE}/glim/states/error_transparent.png`,
  celebrating: `${BASE}/glim/states/celebrating_transparent.png`,
});

export const ARC_ASSETS = Object.freeze({
  master: `${BASE}/arc/masters/arc_master.svg`,
  clay: `${BASE}/arc/svg/arc_clay.svg`,
  creamOnInk: `${BASE}/arc/svg/arc_cream_on_ink.svg`,
  monoInk: `${BASE}/arc/svg/arc_mono_ink.svg`,
  monoPaper: `${BASE}/arc/svg/arc_mono_paper.svg`,
  chainSvg: `${BASE}/arc/chain/intelligence_chain.svg`,
  chainPng: `${BASE}/arc/chain/intelligence_chain.png`,
  motifs: Object.freeze({
    connect: `${BASE}/arc/motifs/motif_connect.png`,
    detect: `${BASE}/arc/motifs/motif_detect.png`,
    opportunity: `${BASE}/arc/motifs/motif_opportunity.png`,
    path: `${BASE}/arc/motifs/motif_path.png`,
    resolve: `${BASE}/arc/motifs/motif_resolve.png`,
    workflowStrip: `${BASE}/arc/motifs/workflow_strip.png`,
  }),
});

// ARC's 15 states (locked package ARC/states, transparent masters). ARC is the
// system / work layer: these are used where Martian's PREPARED WORK is shown
// (never as a face mark, never at equal weight with GLIM in one frame).
export const ARC_STATE_FILES = Object.freeze({
  neutral: `${BASE}/arc/states/neutral_transparent.png`,
  curious: `${BASE}/arc/states/curious_transparent.png`,
  thinking: `${BASE}/arc/states/thinking_transparent.png`,
  discovering: `${BASE}/arc/states/discovering_transparent.png`,
  analyzing: `${BASE}/arc/states/analyzing_transparent.png`,
  recommending: `${BASE}/arc/states/recommending_transparent.png`,
  waiting: `${BASE}/arc/states/waiting_transparent.png`,
  approval_required: `${BASE}/arc/states/approval_transparent.png`,
  acting: `${BASE}/arc/states/acting_transparent.png`,
  success: `${BASE}/arc/states/success_transparent.png`,
  learning: `${BASE}/arc/states/learning_transparent.png`,
  warning: `${BASE}/arc/states/warning_transparent.png`,
  something_changed: `${BASE}/arc/states/changed_transparent.png`,
  error: `${BASE}/arc/states/error_transparent.png`,
  celebrating: `${BASE}/arc/states/celebrating_transparent.png`,
});

// Optical size ladder (SIZE_NOTES.md in the locked package).
export const GLIM_SIZE_LADDER = Object.freeze({ micro: 28, mid: 40, full: 140 });
export const ARC_MIN_PX = 32;
export const BRAND_SAFE_AREA = 0.12; // >= 12 % clear space around any mark

// Lockup copy. Positioning stays "MARTIAN / AI Chief Growth Officer";
// the tagline is the locked one -- do not invent others.
export const BRAND_COPY = Object.freeze({
  name: 'MARTIAN',
  descriptor: 'AI Chief Growth Officer',
  tagline: 'Clarity that moves.',
});
